/* Hand-authored Hindi for the common parent-facing UI. English string → Hindi.
 *
 * Same contract as kn.ts: these are the strings a parent meets constantly (fees, attendance, the
 * feed, the child dashboard, homework, the bus), translated by hand so they render instantly and
 * read correctly. Anything NOT here falls back to the server's AI translation (cached), so the app
 * is complete either way — this file just makes the hot path perfect and free.
 *
 * Keys are the exact English source strings used in t('...') call sites. */
export const hi: Record<string, string> = {
  // ---- global / nav ----
  'Overview': 'अवलोकन',
  'Fees': 'शुल्क',
  'Attendance': 'उपस्थिति',
  'Feed': 'समाचार',
  'From the school': 'विद्यालय से',
  'Communication': 'संचार',
  'News & achievements': 'समाचार और उपलब्धियाँ',
  'School news & achievements': 'विद्यालय समाचार और उपलब्धियाँ',
  'Notifications': 'सूचनाएँ',
  'Sign out': 'साइन आउट',
  'Home': 'होम',
  'Settings': 'सेटिंग्स',
  'Notifications in': 'सूचनाएँ इसमें',

  // ---- fees & payments ----
  'Fees due': 'देय शुल्क',
  'Pay fees': 'शुल्क भुगतान करें',
  'Pay now': 'अभी भुगतान करें',
  'Outstanding': 'बकाया',
  'Paid': 'भुगतान हो गया',
  'Balance': 'शेष राशि',
  'Invoice': 'बिल',
  'Receipt': 'रसीद',
  'Payments': 'भुगतान',
  'All years': 'सभी वर्ष',
  'All fee types': 'सभी शुल्क प्रकार',
  'No payments yet': 'अभी तक कोई भुगतान नहीं',
  'Due': 'देय',
  'Due date': 'देय तिथि',
  'Amount': 'राशि',
  'All fees are paid. Nothing outstanding.': 'सभी शुल्क का भुगतान हो चुका है। कोई बकाया नहीं।',
  'Partly paid': 'आंशिक भुगतान',
  'No invoices yet.': 'अभी तक कोई बिल नहीं।',

  // ---- attendance ----
  'Present': 'उपस्थित',
  'Absent': 'अनुपस्थित',
  'On leave': 'अवकाश पर',
  'Late': 'देर से',
  'Today': 'आज',
  'This month': 'इस महीने',
  'Attendance this month': 'इस महीने की उपस्थिति',
  'Recent days': 'हाल के दिन',
  'No records yet.': 'अभी तक कोई रिकॉर्ड नहीं।',

  // ---- family / child ----
  'No child linked yet': 'अभी तक कोई बच्चा जोड़ा नहीं गया',
  'Link a child': 'बच्चा जोड़ें',
  'Enter the code your school gave you.': 'अपने विद्यालय द्वारा दिया गया कोड दर्ज करें।',
  'Today’s classes': 'आज की कक्षाएँ',
  'Teacher absent today': 'शिक्षक आज अनुपस्थित',
  'My family': 'मेरा परिवार',
  'My school': 'मेरा विद्यालय',
  'Family': 'परिवार',
  'Profile': 'प्रोफ़ाइल',
  'Name': 'नाम',
  'Status': 'स्थिति',
  'Class': 'कक्षा',
  'School': 'विद्यालय',

  // ---- homework & messages ----
  'Homework': 'गृहकार्य',
  'Homework & messages': 'गृहकार्य और संदेश',
  'Messages': 'संदेश',
  'My classroom': 'मेरी कक्षा',
  'No homework': 'कोई गृहकार्य नहीं',
  'No messages': 'कोई संदेश नहीं',
  'These conversations are part of the school record.': 'ये बातचीत विद्यालय के रिकॉर्ड का हिस्सा हैं।',
  'Title': 'शीर्षक',
  'Subject': 'विषय',
  'From': 'से',

  // ---- bus & pickup ----
  'Bus & pickup': 'बस और पिकअप',
  'The bus': 'बस',
  'The gate': 'गेट',
  'Safety': 'सुरक्षा',
  'Pickup pass': 'पिकअप पास',
  'Create a pickup pass': 'पिकअप पास बनाएँ',
  'Cancel this pass': 'यह पास रद्द करें',
  'Collected': 'ले लिया गया',
  'Collecting': 'लेने वाले',
  'Drop location': 'उतरने का स्थान',
  'Valid for': 'मान्य',
  'Transport': 'परिवहन',

  // ---- results & report cards ----
  'Results': 'परिणाम',
  'Report cards': 'रिपोर्ट कार्ड',
  'Marks': 'अंक',
  'Max': 'अधिकतम',
  'Grade': 'श्रेणी',
  'Exams': 'परीक्षाएँ',
  'Timetable': 'समय-सारणी',
  'No published results yet.': 'अभी तक कोई परिणाम प्रकाशित नहीं।',
  'No report cards published yet.': 'अभी तक कोई रिपोर्ट कार्ड प्रकाशित नहीं।',
  'No timetable published for this class yet.': 'इस कक्षा के लिए अभी तक समय-सारणी प्रकाशित नहीं।',

  // ---- requests, remarks, downloads ----
  'Requests': 'अनुरोध',
  'Your requests': 'आपके अनुरोध',
  'No requests yet.': 'अभी तक कोई अनुरोध नहीं।',
  'Remarks': 'टिप्पणियाँ',
  'No remarks yet.': 'अभी तक कोई टिप्पणी नहीं।',
  'Download': 'डाउनलोड',
  'Downloads': 'डाउनलोड',
  'No files to download yet.': 'अभी तक डाउनलोड के लिए कोई फ़ाइल नहीं।',
  'Holidays': 'छुट्टियाँ',
  'No upcoming holidays listed.': 'कोई आगामी छुट्टी सूचीबद्ध नहीं।',

  // ---- store ----
  'Store': 'दुकान',
  'Store & allocations': 'दुकान और आवंटन',
  'Order from the school store': 'विद्यालय की दुकान से ऑर्डर करें',
  'My orders': 'मेरे ऑर्डर',
  'No store items allocated yet.': 'अभी तक कोई सामान आवंटित नहीं।',

  // ---- staff-side nav (shown to staff, harmless for parents) ----
  'Students': 'विद्यार्थी',
  'Admissions': 'प्रवेश',
  'Academics': 'शैक्षणिक',
  'Sessions': 'सत्र',
  'Eligibility': 'पात्रता',
  'Laboratories': 'प्रयोगशालाएँ',
  'Library': 'पुस्तकालय',
  'Hostel': 'छात्रावास',
  'Finance': 'वित्त',
  'Fee admin': 'शुल्क प्रबंधन',
  'Fee recovery': 'शुल्क वसूली',
  'Approvals': 'अनुमोदन',
  'Invoice details': 'बिल विवरण',
  'People': 'कर्मचारी',
  'HR': 'मानव संसाधन',
  'Staff attendance': 'कर्मचारी उपस्थिति',
  'My leave': 'मेरी छुट्टी',
  'Payroll': 'वेतन',
  'Certificates': 'प्रमाणपत्र',
  'Operations': 'संचालन',
  'Release requests': 'रिलीज़ अनुरोध',
  'Student life': 'विद्यार्थी जीवन',
  'Houses & leadership': 'सदन और नेतृत्व',
  'Sports': 'खेल',
  'Announcements': 'घोषणाएँ',
  'School settings': 'विद्यालय सेटिंग्स',
  'Administration': 'प्रशासन',
  'Branding': 'ब्रांडिंग',
  'Public page': 'सार्वजनिक पृष्ठ',
  'Automation': 'स्वचालन',
  'Privacy notice': 'गोपनीयता सूचना',

  // ---- common UI words ----
  'Announcements from the school will appear here.': 'विद्यालय की घोषणाएँ यहाँ दिखाई देंगी।',
  'Awaiting the school’s reply': 'विद्यालय के उत्तर की प्रतीक्षा',
  'Cancel': 'रद्द करें',
  'Date': 'तिथि',
  'Loading…': 'लोड हो रहा है…',
  'Nothing yet': 'अभी कुछ नहीं',
  'Raise a request': 'अनुरोध भेजें',
  'Save': 'सहेजें',
  'Something went wrong': 'कुछ गड़बड़ हो गई',
  'Stop': 'रोकें',
  'Show less': 'कम दिखाएँ',
  'Tap to read more': 'और पढ़ने के लिए टैप करें',
  'You have': 'आपके पास',
  'message': 'संदेश',
  'messages': 'संदेश',
  'unread': 'अपठित',

  // ---- school trips ----
  'Trips': 'यात्राएँ',
  'No trips at the moment.': 'फ़िलहाल कोई यात्रा नहीं।',
  'Going': 'जा रहे हैं',
  'Not going': 'नहीं जा रहे',
  'Your reply needed': 'आपका उत्तर आवश्यक',
  'Withdrawn': 'वापस लिया गया',
  'The school needs your permission before your child can go on this trip.': 'आपके बच्चे के इस यात्रा पर जाने से पहले विद्यालय को आपकी अनुमति चाहिए।',
  'Yes, my child may go': 'हाँ, मेरा बच्चा जा सकता है',
  'No': 'नहीं',
  'Emergency contact': 'आपातकालीन संपर्क',
  'Medical note': 'चिकित्सा टिप्पणी',
  'The trip fee is on your Payments page.': 'यात्रा शुल्क आपके भुगतान पृष्ठ पर है।',
  'Withdraw permission': 'अनुमति वापस लें',
  'School trips': 'विद्यालय यात्राएँ',

  // ---- deliveries ----
  'Your delivery': 'आपकी डिलीवरी',
  'Being packed': 'पैक किया जा रहा है',
  'On the way': 'रास्ते में',
  'Delivered': 'पहुँच गया',
  'Came back to school': 'विद्यालय वापस आ गया',
  'Sent on': 'भेजा गया',
  'Delivered on': 'पहुँचा',
  'Please contact the school office.': 'कृपया विद्यालय कार्यालय से संपर्क करें।',
  'Track': 'ट्रैक करें',
  'No child is linked to your account yet, so a pickup pass cannot be created. Please ask the school office to link your child.': 'आपके खाते से अभी तक कोई बच्चा जुड़ा नहीं है, इसलिए पिकअप पास नहीं बनाया जा सकता। कृपया विद्यालय कार्यालय से संपर्क करें।',
};
